const chapter = {
  id: 'rotational-motion',
  slug: 'rotational-motion',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'System of Particles & Rotational Motion',
        hindi: 'कणों के निकाय और घूर्णन गति',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'com',
      title: {
        english: 'Centre of Mass (COM)',
        hindi: 'द्रव्यमान केन्द्र (COM)',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Centre of mass',
        hindi: 'द्रव्यमान का केंद्र',
          },
          body: {
            english: 'For discrete particles: $\\vec R_{\\text{cm}} = \\dfrac{\\sum m_i \\vec r_i}{\\sum m_i}$. For a continuous body: $\\vec R_{\\text{cm}} = \\dfrac{1}{M}\\int \\vec r\\,dm$.',
        hindi: 'असतत कणों के लिए: $\\vec R_{\\text{cm}} = \\dfrac{\\sum m_i \\vec r_i}{\\sum m_i}$। एक सतत निकाय के लिए: $\\vec R_{\\text{cm}} = \\dfrac{1}{M}\\int \\vec r\\,dm$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'COM of two particles divides line joining them in ratio $m_2 : m_1$ (closer to heavier).',
        hindi: 'दो कणों का COM उन्हें जोड़ने वाली रेखा को $m_2 : m_1$ (भारी के करीब) अनुपात में विभाजित करता है।',
            },
            {
              english: 'COM of uniform rod: midpoint. Triangle (lamina): centroid. Semicircular wire: $2R/\\pi$ from diameter; semicircular disc: $4R/(3\\pi)$.',
        hindi: 'एकसमान छड़ का COM: मध्यबिंदु। त्रिभुज (लैमिना): केन्द्रक. अर्धवृत्ताकार तार: $2R/\\pi$ व्यास से; अर्धवृत्ताकार डिस्क: $4R/(3\\pi)$.',
            },
            {
              english: 'Velocity of COM: $\\vec v_{\\text{cm}} = \\sum m_i \\vec v_i / M$. **Acceleration**: $M\\vec a_{\\text{cm}} = \\vec F_{\\text{ext}}$.',
        hindi: 'COM का वेग: $\\vec v_{\\text{cm}} = \\sum m_i \\vec v_i / M$. **त्वरण**: $M\\vec a_{\\text{cm}} = \\vec F_{\\text{ext}}$.',
            },
            {
              english: 'In absence of external force, COM moves with constant velocity (or stays at rest).',
        hindi: 'बाहरी बल की अनुपस्थिति में, COM स्थिर वेग से चलता है (या स्थिर रहता है)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Two particles of mass 2 kg and 3 kg are at $(0,0)$ and $(5,0)$ m. Find COM.',
        hindi: '2 किग्रा और 3 किग्रा द्रव्यमान के दो कण $(0,0)$ और $(5,0)$ मीटर पर हैं। कॉम खोजें.',
          },
          solution: {
            english: '$x_{\\text{cm}} = (2\\cdot 0 + 3\\cdot 5)/5 = 3$ m. COM at $(3, 0)$.',
        hindi: '$x_{\\text{cm}} = (2\\cdot 0 + 3\\cdot 5)/5 = 3$ मी. $(3,0)$ पर COM।',
          },
        },
      ],
    },
    {
      id: 'rot-kin',
      title: {
        english: 'Rotational Kinematics',
        hindi: 'घूर्णन गतिकी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Angular displacement $\\theta$, angular velocity $\\omega = d\\theta/dt$, angular acceleration $\\alpha = d\\omega/dt$.',
        hindi: 'कोणीय विस्थापन $\\theta$, कोणीय वेग $\\omega = d\\theta/dt$, कोणीय त्वरण $\\alpha = d\\omega/dt$।',
            },
            {
              english: 'For uniform $\\alpha$: $\\omega = \\omega_0 + \\alpha t,\\ \\theta = \\omega_0 t + \\tfrac12 \\alpha t^2,\\ \\omega^2 = \\omega_0^2 + 2\\alpha\\theta$.',
        hindi: 'वर्दी के लिए $\\alpha$: $\\omega = \\omega_0 + \\alpha t,\\ \\theta = \\omega_0 t + \\tfrac12 \\alpha t^2,\\ \\omega^2 = \\omega_0^2 + 2\\alpha\\theta$।',
            },
            {
              english: 'Linear–angular: $v = r\\omega$, $a_t = r\\alpha$ (tangential), $a_c = v^2/r = \\omega^2 r$ (centripetal).',
        hindi: 'रैखिक-कोणीय: $v = r\\omega$, $a_t = r\\alpha$ (स्पर्शरेखा), $a_c = v^2/r = \\omega^2 r$ (केन्द्राभिमुख)।',
            },
          ],
        },
      ],
    },
    {
      id: 'moi',
      title: {
        english: 'Moment of Inertia',
        hindi: 'जड़त्व आघूर्ण',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Moment of inertia',
        hindi: 'जड़ता का क्षण',
          },
          body: {
            english: 'About an axis: $I = \\sum m_i r_i^2 = \\int r^2\\,dm$, where $r$ is perpendicular distance from axis.',
        hindi: 'एक अक्ष के बारे में: $I = \\sum m_i r_i^2 = \\int r^2\\,dm$, जहां $r$ अक्ष से लंबवत दूरी है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Parallel-axis theorem',
        hindi: 'समानांतर-अक्ष प्रमेय',
          },
          statement: {
            english: '$I = I_{\\text{cm}} + M d^2$, where $d$ is distance between the two parallel axes (one through COM).',
        hindi: '$I = I_{\\text{cm}} + M d^2$, जहां $d$ दो समानांतर अक्षों (COM के माध्यम से एक) के बीच की दूरी है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Perpendicular-axis theorem (planar bodies only)',
        hindi: 'लंबवत-अक्ष प्रमेय (केवल समतलीय निकाय)',
          },
          statement: {
            english: '$I_z = I_x + I_y$ for a lamina lying in $xy$-plane.',
        hindi: '$xy$-प्लेन में पड़ी एक लैमिना के लिए $I_z = I_x + I_y$।',
          },
        },
        {
          type: 'table',
          headers: ['Body', 'Axis', '$I$'],
          rows: [
            ['Thin rod (length $L$)', 'through COM, ⟂ rod', '$ML^2/12$'],
            ['Thin rod', 'through end, ⟂ rod', '$ML^2/3$'],
            ['Ring (radius $R$)', 'through centre, ⟂ plane', '$MR^2$'],
            ['Ring', 'diameter', '$MR^2/2$'],
            ['Disc', 'through centre, ⟂ plane', '$MR^2/2$'],
            ['Disc', 'diameter', '$MR^2/4$'],
            ['Solid sphere', 'diameter', '$\\tfrac{2}{5}MR^2$'],
            ['Hollow sphere', 'diameter', '$\\tfrac{2}{3}MR^2$'],
            ['Solid cylinder', 'axis', '$\\tfrac{1}{2}MR^2$'],
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Radius of gyration',
        hindi: 'परिभ्रमण की त्रिज्या',
          },
          body: {
            english: '$I = M K^2 \\Rightarrow K = \\sqrt{I/M}$.',
        hindi: '$I = M K^2 \\Rightarrow K = \\sqrt{I/M}$.',
          },
        },
      ],
    },
    {
      id: 'torque-am',
      title: {
        english: 'Torque, Angular Momentum & Newton\'s Law for Rotation',
        hindi: 'टॉर्क, कोणीय गति और घूर्णन के लिए न्यूटन का नियम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Torque',
        hindi: 'टॉर्क',
          },
          body: {
            english: '$\\vec\\tau = \\vec r \\times \\vec F$. Magnitude $= rF\\sin\\theta$. SI unit: N·m.',
        hindi: '$\\vec\\tau = \\vec r \\times \\vec F$. परिमाण $= rF\\sin\\theta$. एसआई इकाई: एनएम.',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Angular momentum',
        hindi: 'कोणीय गति',
          },
          body: {
            english: '$\\vec L = \\vec r \\times \\vec p = I\\vec\\omega$ (rigid body, fixed axis).',
        hindi: '$\\vec L = \\vec r \\times \\vec p = I\\vec\\omega$ (कठोर शरीर, स्थिर अक्ष)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Newton\'s second law (rotation)',
        hindi: 'न्यूटन का दूसरा नियम (रोटेशन)',
          },
          statement: {
            english: '$\\vec\\tau_{\\text{ext}} = \\dfrac{d\\vec L}{dt} = I\\vec\\alpha$ (constant $I$).',
        hindi: '$\\vec\\tau_{\\text{ext}} = \\dfrac{d\\vec L}{dt} = I\\vec\\alpha$ (निरंतर $I$).',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Conservation of angular momentum',
        hindi: 'कोणीय गति का संरक्षण',
          },
          statement: {
            english: 'If $\\vec\\tau_{\\text{ext}} = 0$, then $\\vec L = $ const. Used in: ice-skater spin, planetary motion (Kepler\'s 2nd law).',
        hindi: 'यदि $\\vec\\tau_{\\text{ext}} = 0$, तो $\\vec L = $ स्थिरांक। इसमें उपयोग किया जाता है: आइस-स्केटर स्पिन, ग्रहीय गति (केपलर का दूसरा नियम)।',
          },
        },
      ],
    },
    {
      id: 'rolling',
      title: {
        english: 'Rolling Motion',
        hindi: 'लोटनिक गति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A body **rolls without slipping** if $v_{\\text{cm}} = R\\omega$ (and $a_{\\text{cm}} = R\\alpha$). Total KE:',
        hindi: 'एक पिंड **बिना फिसले लुढ़कता है** यदि $v_{\\text{cm}} = R\\omega$ (और $a_{\\text{cm}} = R\\alpha$)। कुल केई:',
          },
        },
        {
          type: 'equation',
          latex: 'K_{\\text{total}} = \\tfrac12 M v_{\\text{cm}}^2 + \\tfrac12 I_{\\text{cm}} \\omega^2 = \\tfrac12 M v^2 \\left(1 + \\tfrac{K^2}{R^2}\\right).',
        },
        {
          type: 'theorem',
          name: {
            english: 'Rolling down an incline (without slipping)',
        hindi: 'एक ढलान पर लुढ़कना (बिना फिसले)',
          },
          statement: {
            english: '$a = \\dfrac{g\\sin\\theta}{1 + I_{\\text{cm}}/(MR^2)}$. For solid sphere: $a = \\tfrac{5}{7}g\\sin\\theta$. For ring: $a = \\tfrac12 g\\sin\\theta$.',
        hindi: '$a = \\dfrac{g\\sin\\theta}{1 + I_{\\text{cm}}/(MR^2)}$. ठोस गोले के लिए: $a = \\tfrac{5}{7}g\\sin\\theta$. रिंग के लिए: $a = \\tfrac12 g\\sin\\theta$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Friction (static) provides torque for rolling; in pure rolling no work is done by it.',
        hindi: 'घर्षण (स्थैतिक) रोलिंग के लिए टॉर्क प्रदान करता है; शुद्ध रोलिंग में इसके द्वारा कोई कार्य नहीं किया जाता है।',
            },
            {
              english: 'Order of arrival down incline (fastest first): solid sphere > solid cylinder > hollow sphere > ring.',
        hindi: 'नीचे की ओर आने का क्रम (सबसे तेज़ पहले): ठोस गोला > ठोस सिलेंडर > खोखला गोला > वलय।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A solid sphere rolls down a 30° incline of length 4 m starting from rest. Speed at bottom? ($g=10$)',
        hindi: 'एक ठोस गोला आराम से शुरू करके 4 मीटर लंबाई की 30° ढलान पर लुढ़कता है। सबसे नीचे गति? ($g=10$)',
          },
          solution: {
            english: '$v^2 = 2as$, $a = (5/7) g\\sin 30° = 25/7$. $v^2 = 2(25/7)(4) = 200/7 \\Rightarrow v \\approx 5.35$ m/s.',
        hindi: '$v^2 = 2as$, $a = (5/7) g\\sin 30° = 25/7$. $v^2 = 2(25/7)(4) = 200/7 \\Rightarrow v \\approx 5.35$ मी/से.',
          },
        },
      ],
    },
    {
      id: 'equilibrium',
      title: {
        english: 'Equilibrium of Rigid Bodies',
        hindi: 'दृढ़ पिण्डों की साम्यावस्था',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Translational equilibrium: $\\sum \\vec F = 0$.',
        hindi: 'अनुवादात्मक संतुलन: $\\sum \\vec F = 0$।',
            },
            {
              english: 'Rotational equilibrium: $\\sum \\vec\\tau = 0$ about ANY axis.',
        hindi: 'घूर्णी संतुलन: किसी भी अक्ष के बारे में $\\sum \\vec\\tau = 0$।',
            },
            {
              english: 'Couple: two equal & opposite forces with different lines of action; net force $= 0$, net torque $= F d$ (independent of axis).',
        hindi: 'युगल: अलग-अलग कार्य रेखाओं वाली दो समान और विपरीत शक्तियां; शुद्ध बल $= 0$, शुद्ध टॉर्क $= F d$ (अक्ष से स्वतंत्र)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\vec R_{\\text{cm}} = \\sum m_i \\vec r_i / M',
      desc: {
        english: 'COM',
        hindi: 'COM',
      },
    },
    {
      latex: 'I = \\sum m_i r_i^2',
      desc: {
        english: 'Moment of inertia',
        hindi: 'जड़ता का क्षण',
      },
    },
    {
      latex: 'I = I_{\\text{cm}} + M d^2',
      desc: {
        english: 'Parallel axis',
        hindi: 'समानांतर अक्ष',
      },
    },
    {
      latex: '\\tau = I\\alpha,\\ L = I\\omega',
      desc: {
        english: 'Rotation laws',
        hindi: 'रोटेशन कानून',
      },
    },
    {
      latex: 'K = \\tfrac12 I\\omega^2',
      desc: {
        english: 'Rotational KE',
        hindi: 'घूर्णी केई',
      },
    },
    {
      latex: 'v_{\\text{cm}} = R\\omega',
      desc: {
        english: 'Rolling constraint',
        hindi: 'रोलिंग बाधा',
      },
    },
    {
      latex: 'a = g\\sin\\theta / (1 + K^2/R^2)',
      desc: {
        english: 'Rolling on incline',
        hindi: 'ढलान पर लुढ़कना',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'MOI',
      problem: {
        english: 'Find moment of inertia of a uniform disc of mass $M$ and radius $R$ about a tangent in the plane.',
        hindi: 'द्रव्यमान $M$ और त्रिज्या $R$ की एक समान डिस्क के समतल में स्पर्श रेखा के परितः जड़त्व आघूर्ण ज्ञात कीजिए।',
      },
      answer: {
        english: '$\\tfrac{5}{4} M R^2$',
        hindi: '$\\tfrac{5}{4} M R^2$',
      },
      solution: {
        english: 'About diameter $I_d = MR^2/4$. Parallel axis (distance $R$): $I = MR^2/4 + MR^2 = 5MR^2/4$.',
        hindi: 'व्यास के बारे में $I_d = MR^2/4$। समानांतर अक्ष (दूरी $R$): $I = MR^2/4 + MR^2 = 5MR^2/4$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Rolling',
      problem: {
        english: 'Ratio of rotational to total KE of a solid sphere rolling without slipping.',
        hindi: 'बिना फिसले लुढ़कते हुए एक ठोस गोले के कुल KE का घूर्णी अनुपात।',
      },
      answer: {
        english: '$2/7$',
        hindi: '$2/7$',
      },
      solution: {
        english: '$K_{\\text{rot}}/K_{\\text{total}} = (I/MR^2)/(1 + I/MR^2) = (2/5)/(7/5) = 2/7$.',
        hindi: '$K_{\\text{rot}}/K_{\\text{कुल}} = (I/MR^2)/(1 + I/MR^2) = (2/5)/(7/5) = 2/7$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Conservation of L',
      problem: {
        english: 'A skater spinning at 2 rev/s with arms extended ($I = 5$ kg·m²) pulls arms in to $I = 2$ kg·m². New angular speed?',
        hindi: 'एक स्केटर 2 rev/s पर घूमता हुआ हाथ ($I = 5$ kg·m²) फैलाकर भुजाओं को $I = 2$ kg·m² तक खींचता है। नई कोणीय गति?',
      },
      answer: {
        english: '$5$ rev/s',
        hindi: '$5$ rev/s',
      },
      solution: {
        english: '$I_1\\omega_1 = I_2\\omega_2 \\Rightarrow \\omega_2 = (5)(2)/2 = 5$ rev/s.',
        hindi: '$I_1\\omega_1 = I_2\\omega_2 \\Rightarrow \\omega_2 = (5)(2)/2 = 5$ रेव/एस.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Torque',
      problem: {
        english: 'A force $\\vec F = 2\\hat i + 3\\hat j$ N acts at $\\vec r = \\hat i + \\hat j$ m. Torque about origin?',
        hindi: 'एक बल $\\vec F = 2\\hat i + 3\\hat j$ N $\\vec r = \\hat i + \\hat j$ मीटर पर कार्य करता है। उत्पत्ति के बारे में टोक़?',
      },
      answer: {
        english: '$\\vec\\tau = (3 - 2)\\hat k = \\hat k$ N·m',
        hindi: '$\\vec\\tau = (3 - 2)\\hat k = \\hat k$ N·m',
      },
      solution: {
        english: '$\\vec r \\times \\vec F$.',
        hindi: '$\\vec r \\times \\vec F$।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Rolling on incline',
      problem: {
        english: 'Acceleration of a solid cylinder rolling down a $30°$ incline without slipping ($g=10$).',
        hindi: 'एक ठोस सिलेंडर का $30°$ ढलान पर बिना फिसले लुढ़कने का त्वरण ($g=10$)।',
      },
      answer: {
        english: '$10/3$ m/s²',
        hindi: '$10/3$ m/s²',
      },
      solution: {
        english: '$a = g\\sin\\theta/(1 + 1/2) = (10)(0.5)/(1.5) = 10/3$ m/s².',
        hindi: '$a = g\\sin\\theta/(1 + 1/2) = (10)(0.5)/(1.5) = 10/3$ m/s²। **बैंड सिद्धांत**: वैलेंस बैंड (भरा हुआ), चालन बैंड (), उनके बीच निषिद्ध अंतराल $a = g\\sin\\theta/(1 + 1/2) = (10)(0.5)/(1.5) = 10/3$। इलेक्ट्रॉन ऊर्जा को अवशोषित करके VB→CB पर छलांग लगाते हैं ≥ m/s, जिससे VB में **छेद** निकल जाता है। **आंतरिक** (शुद्ध Si, Ge): $a = g\\sin\\theta/(1 + 1/2) = (10)(0.5)/(1.5) = 10/3$। m/s के साथ चालकता बढ़ती है (अधिक बंधन टूटते हैं)।',
      },
    },
  ],
  next: {
    slug: 'gravitation',
    title: 'Gravitation',
  },
};

export default chapter;
